import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { getAdminJobs } from "../api/admin";
import { AdminJob, AdminJobFilters } from "../types";
import { AppDispatch } from "../redux/store";
import { addNotification } from "../redux/slices/uiSlice";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { Select } from "../components/common/Select";
import { Spinner } from "../components/common/Spinner";
import { JobStatusBadge } from "../components/jobs/JobStatusBadge";
import {
  formatDate,
  formatJobType,
  formatJobNumber,
} from "../utils/format.utils";
import { JOB_TYPE_CONFIGS } from "../services/jobTypes";

export const AdminJobsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const filters: AdminJobFilters = {
        page,
        limit: 20,
      };

      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.job_type = typeFilter;

      const data = await getAdminJobs(filters);
      setJobs(data.jobs);
      setTotal(data.total);
      setPages(data.pages);
    } catch (error: any) {
      dispatch(
        addNotification({
          type: "error",
          message: error.response?.data?.detail || "Failed to fetch jobs",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, dispatch]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  // Build type options - use formatJobType or check config structure
  const typeOptions = [
    { value: "", label: "All Types" },
    ...Object.keys(JOB_TYPE_CONFIGS).map((key) => ({
      value: key,
      label: formatJobType(key), // Use the format utility instead
    })),
  ];

  return (
    <div className="space-y-6 p-10">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">All Jobs</h1>
        <p className="text-slate-400 mt-1">{total} total jobs</p>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={statusOptions}
            />
            <Select
              label="Job Type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              options={typeOptions}
            />
          </div>
        </div>
      </Card>

      {/* Jobs Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400">No jobs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Job
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    User
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Type
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium text-sm">
                    Created
                  </th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium text-sm">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className="border-b border-slate-700/50 hover:bg-slate-700/30"
                  >
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">
                          {formatJobNumber(job.job_number)}
                        </p>
                        <p className="text-slate-400 text-sm truncate max-w-[200px]">
                          {job.filename || job.original_filename || job.id}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white">{job.user_full_name}</p>
                        <p className="text-slate-400 text-sm">
                          {job.user_email}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">
                      {formatJobType(job.job_type)}
                    </td>
                    <td className="py-3 px-4">
                      <JobStatusBadge status={job.status} />
                    </td>
                    <td className="py-3 px-4 text-slate-400 text-sm">
                      {job.created_at ? formatDate(job.created_at) : "-"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end">
                        <Link to={`/jobs/${job.id}`}>
                          <Button size="sm" variant="secondary">
                            View
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700">
            <p className="text-slate-400 text-sm">
              Page {page} of {pages}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setPage(page + 1)}
                disabled={page === pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
